import {
  determineSelectedManagementOption,
  determineSelectedOwnerRepOption,
  isNullUndefinedOrEmptyObject,
  isNullUndefinedOrEmptyString,
} from '../../../common/utils';
import { setAdminUpdateSteps } from '../flow/flowThunks';
import {
  resetAdminUpdateState,
  setAdminUpdateApplicationNumber,
  setAdminUpdateFlowToken,
  setAdminUpdateLicenseError,
  setAdminUpdateLicenseNumber,
  setAdminUpdateLicenseSuccess,
  setDocumentUploadError,
  setDocumentUploadSuccess,
  setInitiatingAdminUpdateFlowError,
  setInitiatingAdminUpdateFlowInProgress,
  setInitiatingAdminUpdateFlowSuccess,
  startAdminUpdateLicense,
  startDocumentUpload,
  updatePermitData,
} from './adminUpdateSlice';

export const initiateAdminUpdateFlow = (operatorToken) => {
  return async (dispatch, getState, api) => {
    let successfull = true;
    try {
      dispatch(setInitiatingAdminUpdateFlowInProgress(true));
      await dispatch(resetAdminUpdateState());
      await dispatch(setAdminUpdateFlowToken(operatorToken));

      // Get license details
      const {
        data: { licenses },
      } = await api.getLicensesRentalscape(getState());
      const [license] = licenses;
      const { applicationNumber, license: licenseNumber } = license;
      const { data } = await api.getLicenseByApplicationNumberRentalscape(
        getState(),
        applicationNumber
      );

      let isResidential = false;
      try {
        const lookupResult = await api.lookupAPNAddress(data.apn);
        if (lookupResult) {
          isResidential = lookupResult?.parcelSpecificInfo?.residential;
        }
      } catch (error) {
        // Nothing to do here - assume this is not residential
      }

      dispatch(setAdminUpdateSteps());

      // get supplementalconfig for each flow
      const supplementalQuestionsConfigApply =
        getState()?.config?.settings?.express?.registration?.application
          ?.questions;
      const supplementalQuestionsConfigUpdate =
        getState()?.config?.settings?.express?.registration?.update?.questions;
      const supplementalQuestionsConfigRenew =
        getState()?.config?.settings?.express?.registration?.renew?.questions;

      // combine configs
      // We need to do this becuase when we call /update we need to send through the supplemental questions, otherwise they are cleared
      const filteredSupplementalQuestionsConfig = [
        ...(supplementalQuestionsConfigApply || []),
        ...(supplementalQuestionsConfigRenew || []),
        ...(supplementalQuestionsConfigUpdate || []),
      ];

      const uniqueSupplementalQuestions =
        filteredSupplementalQuestionsConfig.reduce((acc, current) => {
          const existingIndex = acc.findIndex(
            (item) => item.key === current.key
          );
          // If key does not exist add it else replace existing value
          if (existingIndex === -1) {
            acc.push(current);
          } else {
            acc[existingIndex] = current;
          }
          return acc;
        }, []);

      const supplementalQuestionResults = data.misc?.supplementalQuestions;

      const propertyDetails = {};

      if (!isNullUndefinedOrEmptyObject(filteredSupplementalQuestionsConfig)) {
        propertyDetails.supplementalQuestions = {};

        uniqueSupplementalQuestions.forEach((question) => {
          const key = question.key;

          // Populate supplemental questions object
          propertyDetails.supplementalQuestions[key] = {
            question: question.title,
            questionSubtext: question.description,
            ref: key,
            answer: supplementalQuestionResults?.[key]?.answer,
          };

          // Populate top level fields
          propertyDetails[key] = supplementalQuestionResults?.[key]?.answer;
        });
      }

      const selectedManagementOption = determineSelectedManagementOption(
        data.people,
        true
      );
      const selectedOwnerRepOption = determineSelectedOwnerRepOption(
        data.people,
        true
      );

      const parsedData = {
        property: {
          propertyaddress: data.address,
          propertyid: data.apn,
          unitnumber: data.unitNumber,
          rentalstructure: data.rentalStructure,
          isResidential,
        },
        registrant: {
          registrantType: data.registrantType ?? 'owner', // default registrant type to owner if it does not exist
          owner: Array.isArray(data.people.owner)
            ? data.people.owner
            : [data.people.owner], // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object
          registrant: data.people.registrant,
        },
        initialRegistrantData: data.people.registrant,
        contacts: {
          selectedManagementOption: selectedManagementOption,
          selectedOwnerRepOption: selectedOwnerRepOption,
          propertyManager: data.people?.propertyManager,
          localContact: data.people?.localContact,
        },
        propertyDetails,
      };

      dispatch(setAdminUpdateApplicationNumber(applicationNumber));
      dispatch(setAdminUpdateLicenseNumber(licenseNumber));
      await dispatch(updatePermitData(parsedData));
      await dispatch(setInitiatingAdminUpdateFlowSuccess());
    } catch (err) {
      successfull = false;
      await dispatch(
        setInitiatingAdminUpdateFlowError(
          'Something went wrong while fetching required data.'
        )
      );
    } finally {
      return successfull;
    }
  };
};

export const updateLicense = () => {
  return async (dispatch, getState, api) => {
    let wasSuccesfull = true;
    let hasDocumentUploadError = false;

    try {
      dispatch(startAdminUpdateLicense());

      // UPDATE LICENSE
      const applicationNumber = getState().adminUpdate?.applicationNumber;
      await api.updateLicenseRentalscape(getState(), applicationNumber);

      // UPLOAD DOCUMENTS
      const { documents } = getState().adminUpdate?.permitData;
      if (documents) {
        try {
          dispatch(startDocumentUpload());

          // Each type of document may have zero or more associated files
          // but requires only one upload token per type
          // So we will store the configuration details for each document type in an array
          const uploadConfiguration = [];

          // Get a list of the document types (documents are stored in Redux with the document type as their key)
          const documentTypes = Object.keys(documents);
          // Upload the documents using the new license number
          for (const documentType in documentTypes) {
            const docTypeName = documentTypes[documentType];
            // Get an upload token for each document type
            const { uploadToken } =
              await api.fetchDocumentUploadTokenRentalscape(
                applicationNumber,
                docTypeName,
                {},
                getState()
              );

            // Store the token and associated files in the array as its own config object
            uploadConfiguration.push({
              document: documentType,
              uploadToken: uploadToken,
              files: documents[docTypeName],
            });
          }

          if (
            !uploadConfiguration ||
            uploadConfiguration.length === 0 ||
            uploadConfiguration.filter((config) => config.files.length === 0)
              .length === uploadConfiguration.length
          ) {
            // If there is no uploadConfiguration, it's empty or it only contains configs with no associated files, then we're done.
            dispatch(setDocumentUploadSuccess());
          } else {
            // Do the upload
            for (const uploadConfig of uploadConfiguration) {
              const { files } = uploadConfig;
              const { attachedFiles } = files;
              for (const documentFile of attachedFiles) {
                const { blobFile } = documentFile;
                await api.uploadDocument({
                  uploadToken: uploadConfig.uploadToken,
                  file: blobFile,
                });
              }
            }
            dispatch(setDocumentUploadSuccess());
          }
        } catch (err) {
          wasSuccesfull = false;
          hasDocumentUploadError = true;
        }

        if (hasDocumentUploadError) {
          dispatch(
            setDocumentUploadError(
              'An unexpected error occurred while attempting to upload documents.'
            )
          );
        }
      }
    } catch (err) {
      wasSuccesfull = false;
    } finally {
      if (wasSuccesfull) {
        dispatch(setAdminUpdateLicenseSuccess());
      } else {
        dispatch(
          setAdminUpdateLicenseError(
            'An unexpected error occurred while attempting to update.'
          )
        );
      }
      return wasSuccesfull;
    }
  };
};

// in the admin update mode, we don't want to include the licnese being update in the existing permits warning
export const getExistingLicensesForAPN = (
  apn,
  unitNumber = '',
  licenseNumberToExclude = '',
  applicationNumberToExclude = ''
) => {
  return async (dispatch, getState, api) => {
    let { data } = await api.getLicenseByApnRentalscape(
      getState(),
      apn,
      unitNumber
    );
    let result = {
      inUse: false,
      details: undefined,
    };
    if (data && data.licenses?.length > 0) {
      // Filter out licenses that are of type "application" and match applicationNumberToExclude or
      // that are type of "license" and match licenseNumberToExclude
      const filteredLicenses = data.licenses.filter((l) => {
        const excludeBasedOnApplicationNumber =
          !isNullUndefinedOrEmptyString(applicationNumberToExclude) &&
          l.type === 'application' &&
          l.number === applicationNumberToExclude;

        const excludeBasedOnLicenseNumber =
          !isNullUndefinedOrEmptyString(licenseNumberToExclude) &&
          l.type === 'license' &&
          l.number === licenseNumberToExclude;

        // exclude license if at least one condition is met
        return !(
          excludeBasedOnApplicationNumber || excludeBasedOnLicenseNumber
        );
      });

      if (filteredLicenses.length > 0) {
        result.inUse = true;
        result.details = [
          ...data.licenses.map((l) => {
            return {
              type: l.type,
              registrantName: l.registrant.name,
              permitNumber: l.number,
              propertyAddress: data.address,
              expirationDate: l.registrant.expiryDate,
            };
          }),
        ];
      }
    }
    return result;
  };
};

export const validateCognitoEmailAddress = (emailAddress) => {
  return async (dispatch, _getState, api) => {
    try {
      const result =
        await api.getCognitoUserIdByEmailAddressAsync(emailAddress);

      const accountExists = Object.keys(result).length !== 0;
      return {
        userExists: accountExists,
        verifiedSuccessfully: true,
        errorMessage: null,
      };
    } catch (err) {
      //if an error occurs treat it though ann account does not exist
      return {
        userExists: false,
        verifiedSuccessfully: false,
      };
    }
  };
};

export const getUserDetailsFromEmailAddress = (emailAddress) => {
  return async (dispatch, _getState, api) => {
    try {
      const result = await api.getUserByEmailAddressRentalscapeAsync(
        _getState(),
        emailAddress
      );

      return {
        successful: true,
        result: result,
      };
    } catch (err) {
      return {
        successful: false,
        result: null,
      };
    }
  };
};
